/**
 * The ordering arm applied after the tier balancer has chosen the rows.
 *
 * `ranked` keeps the heuristic order. `shuffled` reorders the idle composer
 * with a weight, so the jobs clinicians actually send — today's list, tomorrow's
 * pre-chart, a clinical question — land earlier more often than a setup chip.
 * The set of rows does not change. Focused shelves never shuffle.
 */
import type { WorkHomeRankingPolicy } from '../../../contracts';

import { workHomeFocus, type WorkContext } from './work-home-context';
import type { WorkHomeSuggestion } from './work-home-suggestions';

/** mulberry32: small, deterministic, good enough to permute four rows. */
const seededRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state = (state + 0x6d2b79f5) >>> 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

export const seededShuffle = <T>(rows: readonly T[], seed: number): readonly T[] => {
  const next = seededRandom(seed);
  const out = [...rows];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(next() * (i + 1));
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
};

/**
 * Weighted sample without replacement. A heavier row wins a slot more often;
 * the same seed always returns the same rows in the same order.
 */
export const weightedSample = <T>(
  rows: readonly T[],
  count: number,
  seed: number,
  weight: (row: T) => number,
  tieBreak: (row: T) => string
): readonly T[] => {
  if (count <= 0 || rows.length === 0) return [];
  const next = seededRandom(seed);
  const keyed = rows.map((row) => {
    const uniform = Math.max(next(), Number.EPSILON);
    const rowWeight = Math.max(weight(row), Number.EPSILON);
    return { row, key: uniform ** (1 / rowWeight) };
  });
  keyed.sort((a, b) => b.key - a.key || tieBreak(a.row).localeCompare(tieBreak(b.row)));
  return keyed.slice(0, Math.min(count, rows.length)).map((entry) => entry.row);
};

export const mintWorkHomeShuffleSeed = (): number => Math.floor(Math.random() * 2 ** 31);

/**
 * Relative to 1. These are the jobs that show up in what clinicians send:
 * today's outstanding list, tomorrow's pre-chart, and a clinical question.
 */
const WORK_HOME_SEND_WEIGHT: Readonly<Record<string, number>> = {
  'clinic-job-list': 3,
  'tray-pre-chart': 3,
  'ask-clinical-question': 3,
  'tray-look-up-treatment': 3,
  'promised-follow-ups': 2,
  'draft-from-pasted-notes': 2,
  'find-patients-with-condition': 2,
};

export const workHomeSendWeight = (row: { id: string }): number =>
  WORK_HOME_SEND_WEIGHT[row.id] ?? 1;

export const applyWorkHomeRankingPolicy = (
  rows: readonly WorkHomeSuggestion[],
  context: WorkContext
): readonly WorkHomeSuggestion[] => {
  const policy: WorkHomeRankingPolicy = context.rankingPolicy ?? 'ranked';
  const shuffle = policy === 'shuffled' && workHomeFocus(context).kind === 'none';
  const ordered = shuffle
    ? weightedSample(
        rows,
        rows.length,
        context.shuffleSeed ?? 0,
        workHomeSendWeight,
        (row) => row.id
      )
    : rows;
  return ordered.map((row) => ({
    ...row,
    analytics: { ...row.analytics, ranking_policy: policy },
  }));
};
